<template>
  <div class="ptt-controls">
    <WorkgroupCentralSelector v-model="wgCentral" />

    <div class="target-selector-container">
      <el-radio-group v-model="selectedTarget" class="target-selector" size="small">
        <el-radio-button label="fleet">Flota</el-radio-button>
        <el-radio-button label="taxi">Taxi</el-radio-button>
      </el-radio-group>
    </div>

    <taxi-license-input
      v-if="selectedTarget === 'taxi'"
      v-model="taxiId"
      :workgroup-id="wgId"
      class="taxi-license"
      error-message="Licencia inválida"
      placeholder="Nº licencia"
    />

    <div class="buttons">
      <button
        class="control-button"
        :class="{ recording: isRecording }"
        @mousedown="startRecording"
        @mouseup="stopRecording"
        @mouseleave="stopRecording"
        @touchstart.prevent="startRecording"
        @touchend.prevent="stopRecording"
      >
        <i class="fas fa-microphone"></i>
      </button>
    </div>

    <div ref="waveformRef" class="waveform"></div>

    <div class="buttons">
      <button class="control-button" :disabled="!hasRecording" @click="playRecording">
        <i class="fas" :class="isPlaying ? 'fa-pause' : 'fa-play'"></i>
      </button>
      <button class="control-button clear danger" :disabled="!hasRecording" @click="clearRecording">
        <i class="fas fa-trash"></i>
      </button>
      <button
        class="control-button send"
        :disabled="!hasRecording || (selectedTarget === 'taxi' && !taxiId)"
        @click="sendRecording"
      >
        <i class="fas fa-paper-plane"></i>
      </button>
    </div>

    <div>
      <el-tag type="danger" size="small">Funcionalidad en desarrollo</el-tag>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from "vue";
import WaveSurfer from "wavesurfer.js";
import TaxiLicenseInput from "@/components/common/forms/TaxiLicenseInput.vue";
import WorkgroupCentralSelector from "@/components/common/forms/WorkgroupCentralSelector.vue";
import { RecordingData } from "../types";
import { useStore } from "@/utils/useStore";

const store = useStore();

const waveformRef = ref<HTMLDivElement | null>(null);
const wavesurfer = ref<WaveSurfer | null>(null);
const mediaRecorder = ref<MediaRecorder | null>(null);
const audioChunks = ref<Blob[]>([]);
const isRecording = ref(false);
const isPlaying = ref(false);
const hasRecording = ref(false);
const selectedTarget = ref("fleet");
const taxiId = ref();

const wgCentral = ref(store.getters["app/defaultSelectedWorkgroupAndCentral"]);

const wgId = computed(() => wgCentral.value[0].wgId);
const centralId = computed(() => wgCentral.value[0].centralId);

let audioContext: AudioContext | null = null;
let analyser: AnalyserNode | null = null;
let mediaStreamSource: MediaStreamAudioSourceNode | null = null;
let animationFrame: number | null = null;

onMounted(async () => {
  if (waveformRef.value) {
    wavesurfer.value = WaveSurfer.create({
      container: waveformRef.value,
      waveColor: "#4a5568",
      progressColor: "#48bb78",
      height: 50,
      cursorWidth: 0,
      interact: false,
      normalize: true,
      barWidth: 2,
      barGap: 1,
    });
  }

  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    // Create MediaRecorder with M4A format
    const mimeType = "audio/mp4;codecs=mp4a.40.2";
    if (MediaRecorder.isTypeSupported(mimeType)) {
      mediaRecorder.value = new MediaRecorder(stream, {
        mimeType,
        audioBitsPerSecond: 128000, // 128kbps for good quality
      });
    } else {
      // Fallback to default format if M4A is not supported
      mediaRecorder.value = new MediaRecorder(stream);
    }

    audioContext = new AudioContext();
    analyser = audioContext.createAnalyser();
    mediaStreamSource = audioContext.createMediaStreamSource(stream);
    mediaStreamSource.connect(analyser);
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.6;

    mediaRecorder.value.ondataavailable = (event) => {
      audioChunks.value.push(event.data);
    };

    mediaRecorder.value.onstop = async () => {
      stopVisualization();
      const audioBlob = new Blob(audioChunks.value, {
        type: mediaRecorder.value?.mimeType || "audio/mp4",
      });
      const audioUrl = URL.createObjectURL(audioBlob);
      if (wavesurfer.value) {
        await wavesurfer.value.load(audioUrl);
      }
      hasRecording.value = true;
    };
  } catch (err) {
    console.error("Error accessing microphone:", err);
  }
});

onUnmounted(() => {
  stopVisualization();
  wavesurfer.value?.destroy();
  audioContext?.close();
});

const startVisualization = () => {
  if (!analyser || !wavesurfer.value) return;

  const bufferLength = analyser.frequencyBinCount;
  const dataArray = new Uint8Array(bufferLength);

  const draw = () => {
    if (!isRecording.value) return;

    analyser!.getByteFrequencyData(dataArray);
    wavesurfer.value!.empty();

    const audioBuffer = new AudioBuffer({
      length: dataArray.length,
      numberOfChannels: 1,
      sampleRate: 44100,
    });
    audioBuffer.copyToChannel(new Float32Array(dataArray), 0);

    const blob = new Blob([audioBuffer as any], { type: "audio/wav" });
    const peaks = [audioBuffer.getChannelData(0)];
    const duration = audioBuffer.duration;
    wavesurfer.value!.loadBlob(blob, peaks, duration);

    animationFrame = requestAnimationFrame(draw);
  };

  animationFrame = requestAnimationFrame(draw);
};

const stopVisualization = () => {
  if (animationFrame !== null) {
    cancelAnimationFrame(animationFrame);
    animationFrame = null;
  }
};

const startRecording = () => {
  if (!mediaRecorder.value || isRecording.value) return;

  wavesurfer.value?.empty();
  audioChunks.value = [];
  hasRecording.value = false;
  mediaRecorder.value.start();
  isRecording.value = true;
  startVisualization();
};

const stopRecording = () => {
  if (!mediaRecorder.value || !isRecording.value) return;

  mediaRecorder.value.stop();
  isRecording.value = false;
};

const playRecording = () => {
  if (!wavesurfer.value) return;

  if (isPlaying.value) {
    wavesurfer.value.pause();
  } else {
    wavesurfer.value.play();
  }
  isPlaying.value = !isPlaying.value;
};

const clearRecording = () => {
  wavesurfer.value?.empty();
  audioChunks.value = [];
  hasRecording.value = false;
  isPlaying.value = false;
};

const sendRecording = () => {
  if (audioChunks.value.length === 0) return;
  const audioBlob = new Blob(audioChunks.value, {
    type: mediaRecorder.value?.mimeType || "audio/mp4",
  });
  emit("send-recording", {
    audio: audioBlob,
    wgId: wgId.value,
    centralId: centralId.value,
    target: selectedTarget.value,
    taxiId: taxiId.value,
  });
};

const emit = defineEmits({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  "send-recording": (data: RecordingData) => {},
});
</script>

<style scoped>
.ptt-controls {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
}

.buttons {
  display: flex;
  justify-content: center;
  gap: 1rem;
}

.control-button {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: #4a5568;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.control-button:hover:not(:disabled) {
  background: #2d3748;
  transform: scale(1.05);
}

.control-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.control-button.recording {
  background: #e53e3e !important; /* Force override */
  animation: pulse 2s infinite;
}

.control-button.recording:hover {
  background: #c53030 !important;
}

.control-button.send {
  background: #48bb78;
}

.control-button.clear.danger {
  background: #e53e3e;
}

.control-button.danger:hover:not(:disabled) {
  background: #c53030;
}

.waveform {
  width: 100%;
  background: #f7fafc;
  border-radius: 4px;
  padding: 0.5rem;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(229, 62, 62, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(229, 62, 62, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(229, 62, 62, 0);
  }
}

.target-selector-container {
  display: flex;
  justify-content: center;
}
</style>
